import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { ListFilterComponent } from '@shared/modules/list/components/list-filter/list-filter.component';
import { ListService } from '@shared/modules/list/services/list.service';
import { TranslateService } from '@ngx-translate/core';
import { AppInjector } from '@shared/services/app-injector.service';
import { ListConfig } from '@shared/modules/list/interfaces/list-config.interface';
import { ListEvent, ListEventType } from '@shared/modules/list/model/list-event.model';
import { ListTaskEventType, ListTaskService } from '@shared/modules/list/services/list-task.service';
import { Subscription } from 'rxjs';
import { ListTasksController } from '@shared/modules/list/components/list-tasks/controllers/list-tasks.controller';
import { StorageService } from '@core/services/storage.service';
import { DndDropEvent } from 'ngx-drag-drop';
import { SnackBarService } from '@core/services/snackbar.service';
import { Project } from '@modules/projects/shared/models/project.model';
import { ActivatedRoute } from '@angular/router';
import { PermissionsGroups } from '@core/permissions/permissions.group';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { TaskSidenavController } from '@shared/modules/task-sidenav/controllers/task-sidenav.controller';
import { TaskType } from '@shared/enums/task-type.enum';
import { map, shareReplay, startWith } from 'rxjs/operators';

@Component({
  selector: 'list-tasks',
  templateUrl: './list-tasks.component.html',
  styleUrls: ['./list-tasks.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListTasksComponent implements OnInit, AfterViewInit, OnDestroy {
  PermissionsGroups = PermissionsGroups;
  ButtonSize = ButtonSize;
  ButtonTypes = ButtonTypes;

  zone: NgZone;
  listTasksController: ListTasksController;
  taskSidenavCtrl: TaskSidenavController;
  sub: Subscription = new Subscription();

  private _config: ListConfig;
  @Input()
  set config(config: ListConfig) {
    this._config = config;
    this.changes.detectChanges();
  }

  get config() {
    return this._config;
  }

  private _showCompleted: boolean = true;
  @Input()
  set showCompleted(showCompleted: boolean) {
    this._showCompleted = showCompleted;
  }

  get showCompleted() {
    return this._showCompleted;
  }

  @Input() filters;
  @Input() isMyWork: boolean = false;
  @Input() searchName: string;
  @Input() projectId?: number;
  @Input() project?: Project;
  @Input() isSearch: boolean = true;

  @ViewChild('filtersComponent') filtersComponent: ListFilterComponent;
  @ViewChild('container', { read: ViewContainerRef }) container: ViewContainerRef;
  @ViewChild('withoutProjectTasksContainer', { read: ViewContainerRef })
  withoutProjectTasksContainer: ViewContainerRef;
  @ViewChild('projectTasksContainer', { read: ViewContainerRef }) projectTasksContainer: ViewContainerRef;

  protected loading = signal(true);

  readonly isDelegatedTasksTab$ = this.activatedRoute.queryParamMap.pipe(
    startWith(this.activatedRoute.snapshot.queryParamMap),
    map((p) => p.get('delegatedTasks') === '1'),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    public service: ListService,
    public listTaskService: ListTaskService,
    public t: TranslateService,
    public changes: ChangeDetectorRef,
    public store: StorageService,
    public resolver: ComponentFactoryResolver,
    public s: SnackBarService,
    private activatedRoute: ActivatedRoute,
    private snackBarService: SnackBarService
  ) {
    this.zone = AppInjector.getInjector().get(NgZone);
    this.taskSidenavCtrl = new TaskSidenavController();
    this.listTasksController = new ListTasksController(
      this.resolver,
      this.changes,
      this.zone,
      this.listTaskService,
      this.store,
      this.service,
      this.s,
      this.t
    );
  }

  ngOnInit(): void {
    this.service.clearService();
    this.service.config = this.config;
    const taskType = this.activatedRoute.snapshot.data.type;
    this.service.setParams('type', taskType || TaskType.TYPE_TASK);
    this.service.config.limit = this.service.PerPage as number;
    this.listenListServiceEvents();
    this.changes.detectChanges();
  }

  ngAfterViewInit() {
    this.listTaskService.project = this.project;
    this.listTaskService.projectId = this.projectId;
    this.listTaskService.isMyWorkTaskLists = this.isMyWork;
    this.listTasksController.container = this.container;
    this.listTasksController.withoutProjectTasksContainer = this.withoutProjectTasksContainer;
    this.listTasksController.projectTasksContainer = this.projectTasksContainer;
    this.service.filters = this.filtersComponent;
    this.service.readUrlParams();
    this.service.setInitSorting();
    this.changes.detectChanges();
  }

  listenListServiceEvents() {
    this.sub = this.service.eventEmitter.subscribe((e: ListEvent<any>) => {
      switch (e.type) {
        case ListEventType.END_GET_ROWS:
          this.listTaskService.listGroupsTask = this.service.rows;
          this.listTasksController.onDataLoad();
          this.loading.set(false);
          this.changes.detectChanges();
          break;
      }
    });

    this.sub.add(
      this.listTaskService.emitter.subscribe((e: any) => {
        switch (e.type) {
          case ListTaskEventType.UPDATE_TASK_ORDER:
            this.taskDropEvent(e);
            break;
        }
      })
    );
  }

  dropEvent(e: DndDropEvent, type?: 'project' | 'without_project'): void {
    if (type && type !== e.data.taskGroup.type) {
      this.snackBarService.error(this.t.instant('Projects.actionForbidden'));
      return;
    }

    this.listTasksController.handleReorder(e.data.taskGroup, e.index);
  }

  taskDropEvent(e: any): void {
    this.listTasksController.handleDrop(e.data.event.data, e.data.targetGroup, e.data.index);
  }

  createNewTaskGroup(type: 'project' | 'without_project'): void {
    this.taskSidenavCtrl.addTaskGroup(null, TaskType.TYPE_TASK, type);
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    this.listTasksController.sub.unsubscribe();
    this.listTaskService.isMyWorkTaskLists = false;
  }
}
